import { useState } from "react";
import api, { MAD, apiError } from "../lib/api";
import { useData } from "../hooks/useData";
import { useAuth } from "../context/AuthContext";
import { usePos } from "../context/PosContext";
import { PageHeader, ExportButtons, StatCard, Loader, Empty } from "../components/ui-kit";
import { Modal, Field } from "../components/Modal";
import { exportExcel, exportPDF } from "../lib/exporters";
import { Plus, Trash2, Trash, Percent, TrendingDown, Coins } from "lucide-react";
import { toast } from "sonner";

export const MOTIFS = {
  casse: "Casse",
  dlc: "DLC dépassée",
  erreur_fabrication: "Erreur de fabrication",
  invendu: "Invendu",
  retour_non_recuperable: "Retour B2B non récupérable",
  autre: "Autre",
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export default function Destructions() {
  const { isAdmin, can } = useAuth();
  const { writePosId } = usePos();
  const { data, loading, refetch } = useData("/destructions");
  const { data: products } = useData("/products");
  const { data: productions } = useData("/productions");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ product_id: "", quantite: 1, motif: "invendu", commentaire: "" });

  const openNew = () => { setForm({ product_id: products[0]?.id || "", quantite: 1, motif: "invendu", commentaire: "" }); setOpen(true); };

  const prodById = Object.fromEntries(products.map((p) => [p.id, p]));
  const selProd = prodById[form.product_id];
  const coutPreview = selProd ? selProd.cout_matiere * Number(form.quantite || 0) : 0;
  const manquePreview = selProd ? selProd.prix_vente * Number(form.quantite || 0) : 0;

  // Coût/manque robustes : valeur enregistrée si présente, sinon repli sur le produit courant (anciens enregistrements).
  const rowCout = (x) => {
    if (x.cout_total && x.cout_total > 0) return x.cout_total;
    const p = prodById[x.product_id];
    return p ? round2(p.cout_matiere * (x.quantite || 0)) : 0;
  };
  const rowManque = (x) => {
    if (x.manque_a_gagner != null && x.manque_a_gagner !== "") return x.manque_a_gagner;
    const p = prodById[x.product_id];
    return p ? round2(p.prix_vente * (x.quantite || 0)) : 0;
  };

  const save = async (e) => {
    e.preventDefault();
    if (!writePosId) return toast.error("Sélectionnez un point de vente (en haut) avant d'enregistrer");
    try {
      await api.post(`/destructions?pos_id=${writePosId}`, { ...form, quantite: parseFloat(form.quantite) });
      toast.success("Destruction enregistrée — stock & rentabilité mis à jour");
      setOpen(false); refetch();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  const remove = async (x) => {
    if (!window.confirm("Supprimer cet enregistrement ?")) return;
    await api.delete(`/destructions/${x.id}`); toast.success("Supprimé"); refetch();
  };

  const fmt = (s) => new Date(s).toLocaleDateString("fr-FR");
  const totalQte = data.reduce((a, x) => a + (x.quantite || 0), 0);
  const total = round2(data.reduce((a, x) => a + rowCout(x), 0));
  const totalManque = round2(data.reduce((a, x) => a + rowManque(x), 0));
  const prodQte = productions.reduce((a, p) => a + (p.quantite_produite || 0), 0);
  const pctDestruction = prodQte > 0 ? round2((totalQte / prodQte) * 100) : 0;

  const cols = [
    { header: "Date", value: (r) => fmt(r.date) },
    { header: "Produit", value: (r) => r.product_nom },
    { header: "Quantité", value: (r) => r.quantite },
    { header: "Coût revient unitaire (DH)", value: (r) => (r.cout_matiere_unitaire && r.cout_matiere_unitaire > 0 ? r.cout_matiere_unitaire : (prodById[r.product_id]?.cout_matiere ?? 0)) },
    { header: "Coût total détruit (DH)", value: (r) => rowCout(r) },
    { header: "Manque à gagner (DH)", value: (r) => rowManque(r) },
    { header: "Motif", value: (r) => MOTIFS[r.motif] || r.motif },
    { header: "Employé", value: (r) => r.employe },
    { header: "Commentaire", value: (r) => r.commentaire || "" },
  ];

  return (
    <div>
      <PageHeader title="Destruction Produits" subtitle="Produits détruits, perdus ou jetés — déduits du stock et de la rentabilité.">
        {can("destructions", "export") && <ExportButtons onExcel={() => exportExcel("destructions_tiramessi", cols, data)} onPDF={() => exportPDF("Destructions TIRAMESSI", cols, data)} />}
        {can("destructions", "create") && <button className="tm-btn" onClick={openNew} data-testid="add-destruction-btn"><Plus size={16} /> Enregistrer une perte</button>}
      </PageHeader>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <StatCard label="Quantité totale détruite" value={totalQte} icon={Trash2} testid="destruction-qte" />
        <StatCard label="Coût total détruit" value={MAD(total)} icon={Trash} accent="#F44336" testid="destruction-total" />
        <StatCard label="% destruction / production" value={`${pctDestruction} %`} icon={Percent} accent="#FF9800" testid="destruction-pct" />
        <StatCard label="Manque à gagner estimé" value={MAD(totalManque)} icon={TrendingDown} accent="#D4AF37" testid="destruction-manque" />
      </div>

      {loading ? <Loader /> : data.length === 0 ? <Empty text="Aucune destruction enregistrée." /> : (
        <div className="tm-card overflow-x-auto" data-testid="destructions-table">
          <table className="tm-table">
            <thead>
              <tr><th>Date</th><th>Produit</th><th className="text-right">Qté</th><th className="text-right">Coût unit.</th><th className="text-right">Coût total</th><th className="text-right">Manque à gagner</th><th>Motif</th><th>Employé</th>{can("destructions", "delete") && <th></th>}</tr>
            </thead>
            <tbody>
              {data.map((x) => {
                const cu = x.cout_matiere_unitaire && x.cout_matiere_unitaire > 0 ? x.cout_matiere_unitaire : (prodById[x.product_id]?.cout_matiere ?? 0);
                return (
                <tr key={x.id} data-testid={`destruction-row-${x.id}`}>
                  <td className="text-[var(--tm-muted)] whitespace-nowrap">{fmt(x.date)}</td>
                  <td className="font-semibold">{x.product_nom}</td>
                  <td className="text-right tabular">{x.quantite}</td>
                  <td className="text-right tabular text-[var(--tm-muted)]">{MAD(cu)}</td>
                  <td className="text-right tabular" style={{ color: "#F44336" }}>{MAD(rowCout(x))}</td>
                  <td className="text-right tabular" style={{ color: "#D4AF37" }}>{MAD(rowManque(x))}</td>
                  <td><span className="tm-badge" style={{ background: "var(--tm-surface-2)", color: "var(--tm-muted)" }}>{MOTIFS[x.motif] || x.motif}</span></td>
                  <td className="text-[var(--tm-muted)]">{x.employe}</td>
                  {can("destructions", "delete") && <td className="text-right"><button onClick={() => remove(x)} className="text-[var(--tm-muted)] hover:text-[#F44336]"><Trash2 size={16} /></button></td>}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Enregistrer une perte" testid="destruction-modal">
        <form onSubmit={save}>
          <Field label="Produit">
            <select className="tm-input" value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} data-testid="destruction-product">
              {products.map((p) => <option key={p.id} value={p.id}>{p.nom} — coût {MAD(p.cout_matiere)}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Quantité"><input type="number" step="0.01" min="0" className="tm-input" required value={form.quantite} onChange={(e) => setForm({ ...form, quantite: e.target.value })} data-testid="destruction-qte-input" /></Field>
            <Field label="Motif">
              <select className="tm-input" value={form.motif} onChange={(e) => setForm({ ...form, motif: e.target.value })} data-testid="destruction-motif">
                <option value="casse">Casse</option>
                <option value="dlc">DLC dépassée</option>
                <option value="erreur_fabrication">Erreur de fabrication</option>
                <option value="invendu">Invendu</option>
                <option value="autre">Autre</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="tm-card p-2.5" style={{ background: "var(--tm-bg)" }}>
              <p className="text-[10px] uppercase tracking-wider text-[var(--tm-muted)]">Coût de revient perdu</p>
              <p className="font-semibold tabular mt-0.5" style={{ color: "#F44336" }} data-testid="destruction-cout-preview">{MAD(coutPreview)}</p>
            </div>
            <div className="tm-card p-2.5" style={{ background: "var(--tm-bg)" }}>
              <p className="text-[10px] uppercase tracking-wider text-[var(--tm-muted)]">Manque à gagner</p>
              <p className="font-semibold tabular mt-0.5" style={{ color: "#D4AF37" }} data-testid="destruction-manque-preview">{MAD(manquePreview)}</p>
            </div>
          </div>
          <Field label="Commentaire"><input className="tm-input" value={form.commentaire} onChange={(e) => setForm({ ...form, commentaire: e.target.value })} /></Field>
          <button type="submit" className="tm-btn w-full mt-2" data-testid="destruction-save">Enregistrer la perte</button>
        </form>
      </Modal>
    </div>
  );
}
